import { type ReactNode, useLayoutEffect } from 'react';
import { apiDocsPlugin } from '@backstage/plugin-api-docs';
import { catalogPlugin } from '@backstage/plugin-catalog';
import { catalogImportPlugin } from '@backstage/plugin-catalog-import';
import { orgPlugin } from '@backstage/plugin-org';
import { apis } from './apis';
import { gitlabAuthApiRef, useApi, appThemeApiRef } from '@backstage/core-plugin-api';
import { appLanguageApiRef } from '@backstage/core-plugin-api/alpha';
import {
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/frontend-defaults';
import { IconButton } from '@material-ui/core';
import { UnifiedThemeProvider } from '@backstage/theme';
import { myTheme } from './theme/theme';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  XviaCategoryPickerExtension,
  XviaOrganizationPickerExtension,
  XviaServicePickerExtension,
  XviaSlugPickerExtension,
} from './scaffolder';
import { translationResources } from './translations';
import { getStoredLanguage } from './utils/languageStorage';
import { getStoredTheme } from './utils/themeStorage';
import {
  AppRootWrapperBlueprint,
  SignInPageBlueprint,
  ThemeBlueprint,
  TranslationBlueprint,
} from '@backstage/plugin-app-react';
import {
  ApiBlueprint,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import { navModule } from './modules/nav';
import { catalogModule } from './modules/catalog';
import { orgSectionLabelFeatures } from './modules/orgSectionLabel';
import techdocsPluginRoute from '@backstage/plugin-techdocs/alpha';

const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => props => (
      <SignInPage
        {...props}
        provider={{
          id: 'gitlab-auth-provider',
          title: 'GitLab',
          message: 'Sign in using GitLab',
          apiRef: gitlabAuthApiRef,
        }}
      />
    ),
  },
});

const apiExtensions = apis.map(api =>
  ApiBlueprint.make({
    name: api.api.id,
    params: defineParams => defineParams(api),
  }),
);

const xviaLightTheme = ThemeBlueprint.make({
  name: 'xvia-light-theme',
  params: {
    theme: {
      id: 'xvia-light-theme',
      title: 'X-Via Backstage Theme',
      variant: 'light',
      icon: <IconButton />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={myTheme}>{children}</UnifiedThemeProvider>
      ),
    },
  },
});

const LanguageInitializer = ({ children }: { children: ReactNode }) => {
  const appLanguageApi = useApi(appLanguageApiRef);

  useLayoutEffect(() => {
    const storedLanguage = getStoredLanguage();
    const currentLanguage = appLanguageApi.getLanguage().language;
    if (currentLanguage !== storedLanguage) {
      appLanguageApi.setLanguage(storedLanguage);
    }
  }, [appLanguageApi]);

  return <>{children}</>;
};

const ThemeInitializer = ({ children }: { children: ReactNode }) => {
  const appThemeApi = useApi(appThemeApiRef);
  useLayoutEffect(() => {
    const storedTheme = getStoredTheme();
    if (storedTheme) {
      appThemeApi.setActiveThemeId(storedTheme);
    }
  }, [appThemeApi]);

  return <>{children}</>;
};

const scaffolderCustomizations = createFrontendModule({
  pluginId: 'scaffolder',
  extensions: [
    XviaCategoryPickerExtension,
    XviaOrganizationPickerExtension,
    XviaServicePickerExtension,
    XviaSlugPickerExtension
  ],
});

const translationExtensions = translationResources.map((resource) =>
  TranslationBlueprint.make({
    name: `translation-resource-${resource.id}`,
    params: { resource },
  }),
);

const themeInitializerFeature = AppRootWrapperBlueprint.make({
  name: 'theme-initializer',
  params: {
    component: ThemeInitializer,
  },
});

const languageInitializerFeature = AppRootWrapperBlueprint.make({
  name: 'language-initializer',
  params: {
    component: LanguageInitializer,
  },
});

const app = createApp({
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPluginRoute.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPluginRoute.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  features: [
    scaffolderCustomizations,
    createFrontendModule({
      pluginId: 'app',
      extensions: [
        themeInitializerFeature,
        languageInitializerFeature,
        ...apiExtensions,
        xviaLightTheme,
        signInPage,
        ...translationExtensions,
      ],
    }),
    navModule,
    catalogModule,
    ...orgSectionLabelFeatures,
  ],
});

export default app;
